import { Injectable } from "@angular/core";
import { Platform } from "@ionic/angular";
import { AccountBalanceService } from "../balance/account-balance.service";
import { PurchaseService } from "../purchase/purchase.service";
import { Navigator } from "src/app/components/menu/navigator";
import "cordova-plugin-purchase";
import { ProgressingService } from "src/app/services/progressing/progressing.service";
import { App, AppState } from "@capacitor/app";
import { BehaviorSubject, throwError, of } from "rxjs";
import {
  retryWhen,
  catchError,
  delay,
  take,
  mergeMap,
  tap,
  concatMap,
} from "rxjs/operators";
import { ConfigService } from "../config/config.service";
import { LogService } from "src/app/services/log/log.service";

@Injectable({
  providedIn: "root",
})
export class InappService {
  public isInitialized = false;
  private allOffers;
  private uniquePriceCodes;
  private updatedOffers = [];
  private specialOffersStatus = {};
  private lastTransaction = null;
  private currentTransaction = null;
  private progressUIState = false;
  private updatedOffersSubject: BehaviorSubject<any> = new BehaviorSubject(
    null
  );
  private defaultRetryConfig = {
    retryAttempts: 3,
    timeoutInterval: 5000,
    retryEnabled: true,
  };
  private retryConfig;

  constructor(
    private purchaseService: PurchaseService,
    private balanceService: AccountBalanceService,
    private platform: Platform,
    private navigator: Navigator,
    private config: ConfigService,
    private progressingUI: ProgressingService,
    private logger: LogService
  ) {}

  async initializeStore(offers: any) {
    this.allOffers = offers;
    this.uniquePriceCodes = new Set(offers.map((offer) => offer.priceCode));
    this.registerOffers(offers);
    this.setupStoreEventListeners();
    try {
      this.retryConfig = this.getRetryConfig();
    } catch (e) {
      this.retryConfig = this.defaultRetryConfig;
    }
    // CdvPurchase.store.verbosity = CdvPurchase.LogLevel.DEBUG;
    await CdvPurchase.store.initialize();
  }
  appStateChanger() {
    App.addListener("appStateChange", (state: AppState) => {
      if (state.isActive) {
        if (this.progressUIState) {
          this.showProgressUI("Verifying your purchase");
        }
      }
    });
  }
  appStateRemover() {
    try {
      App.removeAllListeners();
    } catch (e) {
      console.log("Errors", e);
    }
  }
  private registerOffers(offers) {
    const productsToRegister = [];
    offers.forEach((offer) => {
      const product = CdvPurchase.store.get(offer.priceCode);
      this.addToSpecialPurchasesStore(offer);

      if (!product) {
        const newProduct = {
          id: offer.priceCode,
          type: CdvPurchase.ProductType.CONSUMABLE,
          platform: CdvPurchase.Platform.APPLE_APPSTORE,
        };
        if (this.platform.is("android")) {
          newProduct.platform = CdvPurchase.Platform.GOOGLE_PLAY;
        }
        productsToRegister.push(newProduct);
      }
    });

    if (productsToRegister.length) {
      CdvPurchase.store.register(productsToRegister);
    }
  }
  private setupStoreEventListeners(): void {
    CdvPurchase.store.when().productUpdated((product) => {
      this.updatedOffers.push(product);
      if (this.updatedOffers.length == this.uniquePriceCodes.size) {
        this.updatedOffersSubject.next(this.updatedOffers);
      }
    });

    CdvPurchase.store.when().approved((transaction) => {
      transaction.verify();
      if (this.progressUIState) this.showProgressUI("Verifying your purchase");
    });
    CdvPurchase.store.when().unverified((unverified) => {
      console.log(
        `[Store] Unverified. code: ${unverified.payload.code}, ` +
          "status: " +
          unverified.payload.status +
          ", " +
          unverified.payload.message
      );
    });

    CdvPurchase.store.when().verified((receipt) => {
      this.handleVerifiedReceipt(receipt);
    });
    CdvPurchase.store.error((error) => {
      console.error("CdvPurchase ERROR: " + error.message);
      this.hideProgressUI();
    });
  }
  getUpdatedOffers() {
    return this.updatedOffersSubject;
  }
  private hideProgressUI() {
    this.progressUIState = false;
    if (this.progressingUI) {
      this.progressingUI.hide();
      this.appStateRemover();
    }
  }
  private showProgressUI(progressText = "Processing") {
    if (this.progressingUI) {
      this.progressingUI.show(true, progressText);
    }
  }

  purchase(product) {
    this.navigator.dismissAll();
    const productId = product.priceCode;
    this.addToSpecialPurchasesStore(product, true);
    const offer = CdvPurchase.store.get(productId);
    offer.canPurchase;
    offer.getOffer().order();
    this.progressUIState = true;
    this.showProgressUI();
    this.appStateChanger();
  }
  addToSpecialPurchasesStore(product, needDismissAllDialog=false) {
    const productStatus = {
      isPiggyBank: product.is_Piggy_Bank || false,
      isPremium: product.is_premium || false,
      isSpecialOffer: product.isSpecialOffer || false,
      chips: product.chips,
      type: "REGULAR",
      bonusCode: product.priceCode,
      totalOf: 0,
      totalDays: 0,
    };

    if (productStatus.isSpecialOffer) {
      productStatus.type = "SPECIAL_OFFER";
      productStatus.bonusCode = product.bonusCode ?? product.priceCode;
    } else if (productStatus.isPiggyBank) {
      productStatus.type = "PIGGY_BANK";
    } else if (productStatus.isPremium) {
      productStatus.type = "PREMIUM_PURCHASE";
      productStatus.totalOf = product.totalOf ?? productStatus.totalOf;
      productStatus.totalDays = product.totalDays ?? productStatus.totalDays;
      if(needDismissAllDialog){
        this.navigator.dismissAll();
      }
    } else {
      if(needDismissAllDialog){
        this.navigator.dismissAll();
      }
    }
    this.specialOffersStatus[product.priceCode] = productStatus;
  }
  checkIfSpecialPurchase(Item) {
    const productStatus =
      this.specialOffersStatus[Item.priceCode ?? Item.price_code];

    if (productStatus) {
      Item.is_premium = productStatus.isPremium;
      Item.is_special_offer = productStatus.isSpecialOffer;
    }
    if (productStatus.isSpecialOffer || productStatus.chips) {
      Item.chips = productStatus.chips;
    }
    if (productStatus.isSpecialOffer && "bonusCode" in productStatus) {
      Item.price_code = productStatus.bonusCode;
    }
    if (productStatus.isPremium) {
      if ("chips" in productStatus && "totalDays" in productStatus) {
        Item.totalOf =
          productStatus.totalOf ??
          productStatus.chips * productStatus.totalDays;
      } else {
        Item.totalOf = 0;
      }
      if ("totalDays" in productStatus) {
        Item.totalDays = productStatus.totalDays ?? 7;
      } else {
        Item.totalDays = 7;
      }
    }
  }
  private parseReceipt(receipt) {
    let appStoreReceipt,
      purchaseToken,
      productId,
      transactions,
      lastestIosProductId,
      transactionId;

    const isIOS = this.platform.is("ios");

    const parser = (key, value) => {
      if (isIOS) {
        if (key === "appStoreReceipt") {
          appStoreReceipt = value;
        } else if (key === "transactions") {
          transactions = value;
        }
      } else {
        if (key === "purchaseToken") {
          purchaseToken = value;
        } else if (key === "productId") {
          productId = value;
        }
      }
      return value;
    };

    JSON.parse(JSON.stringify(receipt), parser);

    if (isIOS) {
      const filteredTransactions = transactions.filter((transaction) =>
        transaction.hasOwnProperty("purchaseDate")
      );

      const sortedTransactions = filteredTransactions.sort((a, b) => {
        const dateA = new Date(a.purchaseDate).getTime();
        const dateB = new Date(b.purchaseDate).getTime();
        return dateB - dateA;
      });

      if (sortedTransactions.length != 0) {
        if (!this.lastTransaction) {
          this.lastTransaction = sortedTransactions[0];
        } else {
          this.currentTransaction = sortedTransactions[0];
        }

        lastestIosProductId = sortedTransactions[0].products[0].id;
        transactionId = sortedTransactions[0].transactionId;
      }
    } else {
      if (!this.lastTransaction) {
        this.lastTransaction = receipt;
      } else {
        this.currentTransaction = receipt;
      }
    }
    if (isIOS) {
      if (!appStoreReceipt || !lastestIosProductId) {
        console.log("Required data not available for iOS.");

       this.logger.log(
          {
            reason: "Transcation receipt Required data not available for iOS.",
          },
          receipt
        );

        return null;
      }
    } else {
      if (!purchaseToken || !productId) {
        console.log("Required data not available for Android.");

       this.logger.log(
          {
            reason:
              "Transcation receipt Required data not available for Android.",
          },
          receipt
        );
        return null;
      }
    }

    let transactionReceipt;

    if (isIOS) {
      transactionReceipt = {
        payment_type_code: "IOS",
        base64_signedreceipt: appStoreReceipt,
        transactionId: transactionId,
        price_code: lastestIosProductId,
      };
    } else {
      transactionReceipt = {
        payment_type_code: "GP",
        purchaseToken: purchaseToken,
        price_code: productId,
      };
    }

    console.log("New Transaction Receipt", transactionReceipt);
   this.logger.log(
      {
        reason: "New Transaction Receipt",
      },
      transactionReceipt
    );

    return transactionReceipt;
  }
  handleVerifiedReceipt(receipt) {
    console.log("receipt", receipt);
   this.logger.log(
      {
        reason: "Got a Verified Receipt",
      },
      receipt
    );
    let Item;
    let transactionReceipt = this.parseReceipt(receipt);
    if (!transactionReceipt) return;

    transactionReceipt.orginalReceipt = receipt;
    if (
      this.areTransactionsEqual(this.lastTransaction, this.currentTransaction)
    ) {
      this.lastTransaction = null;
      console.log(
        "Transactions sent to webservice already",
        transactionReceipt
      );
     this.logger.log(
        {
          reason: "Duplicate Transcation Found Ignoring",
        },
        this.currentTransaction
      );
      return;
    }

    Item = this.getOfferByPriceCode(
      transactionReceipt.price_code,
      this.specialOffersStatus[transactionReceipt.price_code].type
    );
    this.checkIfSpecialPurchase(Item);
    this.checkIfSpecialPurchase(transactionReceipt);

    if (this.specialOffersStatus[Item.priceCode].isPiggyBank) {
      this.breakPiggyBank(transactionReceipt, Item);
    } else {
      this.purchaseCoins(transactionReceipt, Item);
    }
  }
  areTransactionsEqual(lastTransaction, currentTransaction) {
    if (!lastTransaction || !currentTransaction) {
      return false;
    }
    const lastTransactionID =
      lastTransaction.transactionId || lastTransaction.id;
    const currentTransactionID =
      currentTransaction.transactionId || currentTransaction.id;
    return lastTransactionID === currentTransactionID;
  }

  purchaseCoins(transactionReceipt, Item) {
    this.retryObservable(() =>
      this.purchaseService.purchaseCoins({ ...transactionReceipt })
    ).subscribe({
      next: (response: any) => {
        transactionReceipt.orginalReceipt.finish();
        if (
          response &&
          response.result !== undefined &&
          response.result === 0
        ) {
         this.logger.log(
            {
              reason: "Webservice Returns Failed",
            },
            transactionReceipt
          );
          this.onFailure("PurchaseSuccessFailureComponent");
        } else if (
          response &&
          response.result !== undefined &&
          response.result !== 0
        ) {
          if (response.payload) {
            if (response.payload.boyd_points !== undefined) {
              Item.points = response.payload.boyd_points;
            }
            if (response.payload.tier_credits !== undefined) {
              Item.credits = response.payload.tier_credits;
            }
          }
          this.onSuccess(Item, "PurchaseSuccessFailureComponent");
        } else {
          this.onFailure("PurchaseSuccessFailureComponent");
        }
      },
      error: (error) => {
        if (typeof error === 'function') {
          const actualError = error();
          if (actualError?.error_code == "DUPLICATED_DEPOSIT_REQUEST") {
            transactionReceipt.orginalReceipt.finish();
          }
        }
       this.logger.log(
          {
            reason: "Cannot Connect Webservice Failure",
          },
          transactionReceipt
        );
        this.onFailure("PurchaseSuccessFailureComponent");
      },
    });
  }
  breakPiggyBank(transactionReceipt, Item) {
    this.retryObservable(() =>
      this.purchaseService.breakPiggy({ ...transactionReceipt })
    ).subscribe({
      next: (response: any) => {
        transactionReceipt.orginalReceipt.finish();
        if (
          response &&
          response.result !== undefined &&
          response.result === 0
        ) {
         this.logger.log(
            {
              reason: "Webservice Returned Failed for Piggy",
            },
            transactionReceipt
          );
          this.onFailure("PurchaseSuccessFailureComponent");
        } else if (
          response &&
          response.result !== undefined &&
          response.result !== 0
        ) {
          if (response.payload) {
            if (response.payload.boyd_points !== undefined) {
              Item.points = response.payload.boyd_points;
            }
            if (response.payload.tier_credits !== undefined) {
              Item.credits = response.payload.tier_credits;
            }
          }

          this.onSuccess(Item, "PurchaseSuccessFailureComponent");
        } else {
          this.onFailure("PurchaseSuccessFailureComponent");
        }
      },
      error: (error) => {
       this.logger.log(
          {
            reason: "Cannot Connect Webservice Failure Piggy",
          },
          transactionReceipt
        );
        this.onFailure("PurchaseSuccessFailureComponent");
      },
    });
  }
  onSuccess(Item, component?) {
    const displayMessage = "Points added to your account.";
    this.balanceService.updateBalance();
    this.hideProgressUI();
    this.navigator.navigateTo(
      component,
      {
        customModalClass: "fancy-modal custom-shop-model-class-transparent",
        backdropDismiss: true,
      },
      { status: true, message: displayMessage, ...Item }
    );
  }
  onFailure(component?: string) {
    this.hideProgressUI();
    this.navigator.navigateTo(
      component,
      {
        customModalClass: "fancy-modal custom-shop-model-class-transparent",
        backdropDismiss: true,
      },
      { status: false, message: "Please Contact Support" }
    );
  }
  getOfferByPriceCode(targetPriceCode, type) {
    const foundOffer = this.allOffers.find(
      (offer) => offer.priceCode === targetPriceCode && offer.type == type
    );

    if (foundOffer) {
      return {
        priceCode: foundOffer.priceCode,
        credits: foundOffer.tierCredits,
        chips: foundOffer.chips,
        type: foundOffer.type,
        points: foundOffer.boydPoints,
        price: foundOffer.price,
      };
    } else {
      return null;
    }
  }

  retryObservable(operation) {
    const { retryAttempts, timeoutInterval, retryEnabled } = this.retryConfig;

    return of(null).pipe(
      mergeMap(() => operation()),
      catchError((error) => {
        console.error("Error caught in operation:", error);
        if (error.error_code == "DUPLICATED_DEPOSIT_REQUEST") {
          return throwError(error);
        }
        return throwError(() => new Error("Error in operation: " + error));
      }),
      retryWhen((errors) =>
        errors.pipe(
          tap(() => console.log("Retrying operation...")),
          delay(timeoutInterval),
          take(retryAttempts),
          concatMap((error, count) =>
            count < retryAttempts - 1
              ? of(error).pipe(delay(timeoutInterval))
              : throwError(() => error)
          )
        )
      )
    );
  }

  getRetryConfig() {
    try {
      const serverResponseJson =
        this.config.getConfig().FEATURE_INAPP_RETRY || "{}";
      const parsedResponse = JSON.parse(serverResponseJson);
      console.log("RetryConfig", parsedResponse);
      let platformConfig = this.defaultRetryConfig;

      if (this.platform.is("ios") && this.platform.is("capacitor")) {
        platformConfig = parsedResponse.ios || this.defaultRetryConfig;
      } else if (this.platform.is("android") && this.platform.is("capacitor")) {
        platformConfig = parsedResponse.android || this.defaultRetryConfig;
      } else {
        platformConfig = this.defaultRetryConfig;
      }

      return {
        retryAttempts:
          typeof platformConfig.retryAttempts === "number"
            ? platformConfig.retryAttempts
            : parseInt(platformConfig.retryAttempts, 10) ||
              this.defaultRetryConfig.retryAttempts,
        timeoutInterval:
          typeof platformConfig.timeoutInterval === "number"
            ? platformConfig.timeoutInterval
            : parseInt(platformConfig.timeoutInterval, 10) ||
              this.defaultRetryConfig.timeoutInterval,
        retryEnabled:
          typeof platformConfig.retryEnabled === "boolean"
            ? platformConfig.retryEnabled
            : this.defaultRetryConfig.retryEnabled,
      };
    } catch (error) {
      console.error("Error parsing retry configuration:", error);
      return this.defaultRetryConfig;
    }
  }


}
